import SalesCashHandoverReceiptsApi from 'api/admins/sales-cash-handover-receipts-api'
import { TitlePage } from 'components/atoms'
import { ALERT_TYPES } from 'constants/alert-constants'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import { Button, Card, CardBody, Col, Container, Row, Spinner, Table } from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const NO_CURRENCY_VALUE = 'none'
const MIXED_CURRENCY_VALUE = 'mixed'

const normalizeCurrencyCode = (currency = 'idr') => (
  currency.toString().trim().toUpperCase() || 'IDR'
)

const moneyCurrency = (currency) => (
  currency && currency !== NO_CURRENCY_VALUE && currency !== MIXED_CURRENCY_VALUE
)

const fallbackCurrencyFormat = (number, currency) => (
  `${normalizeCurrencyCode(currency)} ${number.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
)

const formatCurrency = (value, currency = 'idr') => {
  if (currency === NO_CURRENCY_VALUE || currency === MIXED_CURRENCY_VALUE) return '-'

  const number = Number(value || 0)

  if (!Number.isFinite(number)) return '-'

  try {
    const normalizedCurrency = normalizeCurrencyCode(currency)

    return new Intl.NumberFormat(normalizedCurrency === 'IDR' ? 'id-ID' : 'en-US', {
      style                 : 'currency',
      currency              : normalizedCurrency,
      maximumFractionDigits : 0
    }).format(number)
  }
  catch (error) {
    return fallbackCurrencyFormat(number, currency || error?.currency)
  }
}

const formatDateTime = (value) => {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleString('en-GB', {
    day    : '2-digit',
    month  : 'short',
    year   : 'numeric',
    hour   : '2-digit',
    minute : '2-digit'
  })
}

const readFilename = (response, fallback) => {
  const disposition = response?.headers?.['content-disposition']
  if (!disposition) return fallback

  const match = disposition.match(/filename="?([^"]+)"?/i)
  return match?.[1] || fallback
}

const displayValue = (value) => (
  value === undefined || value === null || value === '' ? '-' : value
)

const itemLabel = (item = {}) => (
  [
    item.inventory_sku || item.product_sku,
    item.product_name
  ].filter(Boolean).join(' - ') || item.description || '-'
)

const itemCurrency = (item = {}, receipt = {}) => (
  item.currency || receipt.currency || NO_CURRENCY_VALUE
)

const itemCurrencyLabel = (item = {}, receipt = {}) => (
  item.currency_label || receipt.currency_label || normalizeCurrencyCode(itemCurrency(item, receipt))
)

const SalesCashHandoverReceiptsDetailPage = ({ pageUtils }) => {
  const { id } = useParams()
  const history = useHistory()
  const [ receipt, setReceipt ] = useState({})
  const [ isLoading, setIsLoading ] = useState(true)
  const [ isDownloading, setIsDownloading ] = useState(false)
  const [ isDeleting, setIsDeleting ] = useState(false)

  const items = useMemo(() => (
    Array.isArray(receipt.items) ? receipt.items : []
  ), [ receipt.items ])
  const showValueColumn = items.some((item) => moneyCurrency(itemCurrency(item, receipt)))

  const fetchReceipt = () => {
    setIsLoading(true)

    SalesCashHandoverReceiptsApi.show({ id })
      .then((response) => {
        setReceipt(response?.data?.cash_handover_receipt || {})
      })
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error.response?.data || { message: 'Failed to fetch Tanda Terima General.' })
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    fetchReceipt()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ id ])

  const handleBack = () => {
    history.push({ pathname: '/app/sales/cash-handover-receipts' })
  }

  const handleDownloadPdf = async () => {
    setIsDownloading(true)

    await SalesCashHandoverReceiptsApi.downloadPdf({ id })
      .then((response) => {
        const blob = new Blob([ response.data ], { type: 'application/pdf' })
        const filename = readFilename(response, `${receipt.receipt_number || 'cash-handover-receipt'}.pdf`)
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error.response?.data || { message: 'Failed to download PDF.' })
      })
      .finally(() => setIsDownloading(false))
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this Tanda Terima General?')) return

    setIsDeleting(true)

    await SalesCashHandoverReceiptsApi.destroy({ id })
      .then((response) => {
        pageUtils?.setAlertMsg?.(response?.data?.message || 'Tanda Terima General has been deleted.', ALERT_TYPES.SUCCESS)
        handleBack()
      })
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error.response?.data || { message: 'Failed to delete Tanda Terima General.' })
      })
      .finally(() => setIsDeleting(false))
  }

  const renderDetailRow = (label, value) => (
    <tr>
      <td className='font-weight-bold' style={{ width: '260px' }}>{ label }</td>
      <td>{ displayValue(value) }</td>
    </tr>
  )

  if (isLoading) {
    return (
      <Card body>
        <div className='d-flex align-items-center'>
          <Spinner size='sm' className='mr-2' />
          { 'Loading Tanda Terima General...' }
        </div>
      </Card>
    )
  }

  return (
    <>
      <div className='d-flex justify-content-between align-items-center flex-wrap mb-4'>
        <TitlePage mainTitle={ 'Tanda Terima General Detail' } subTitle={ receipt.receipt_number || '-' } />

        <div>
          <Button color='secondary' outline className='mr-2' onClick={ handleBack }>
            { 'Back to List' }
          </Button>
          { receipt.can_edit && <Button color='warning' className='mr-2' onClick={ () => history.push(`/app/sales/cash-handover-receipts?edit_id=${id}`) }>{ 'Edit / Revisi' }</Button> }
          <Button color='primary' className='mr-2' onClick={ handleDownloadPdf } disabled={ isDownloading }>
            { isDownloading ? 'Preparing...' : 'Download PDF' }
          </Button>
          <Button color='danger' onClick={ handleDelete } disabled={ isDeleting }>
            { isDeleting ? 'Deleting...' : 'Delete' }
          </Button>
        </div>
      </div>

      <Container fluid>
        <Row>
          <Col lg={ 6 }>
            <Card>
              <CardBody>
                <h4>{ 'Receipt Information' }</h4>
                <Table bordered responsive size='sm' className='mb-0'>
                  <tbody>
                    { renderDetailRow('Receipt Number', receipt.receipt_number) }
                    { renderDetailRow('Date', receipt.receipt_date) }
                    { renderDetailRow('Input Mode', receipt.entry_mode) }
                    { renderDetailRow('Currency', receipt.currency_summary_label || receipt.currency_label || normalizeCurrencyCode(receipt.currency)) }
                    { renderDetailRow('Total Value', receipt.value_label || formatCurrency(receipt.value, receipt.currency)) }
                    { renderDetailRow('Created By', receipt.created_by_name) }
                    { renderDetailRow('Created At', formatDateTime(receipt.created_at)) }
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>

          <Col lg={ 6 }>
            <Card>
              <CardBody>
                <h4>{ 'Customer / Handover' }</h4>
                <Table bordered responsive size='sm' className='mb-0'>
                  <tbody>
                    { renderDetailRow('Full Name', receipt.full_name) }
                    { renderDetailRow('Phone Number', receipt.phone_number) }
                    { renderDetailRow('Email', receipt.email) }
                    { renderDetailRow('Given By', receipt.given_by_name) }
                    { renderDetailRow('Received By', receipt.received_by_name) }
                    { renderDetailRow('Received By (2)', receipt.second_received_by_name) }
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={ 12 }>
            <Card>
              <CardBody>
                <h4>{ 'Product / Cash Items' }</h4>
                <Table bordered responsive style={{ tableLayout: 'fixed', minWidth: 850 }}>
                  <thead>
                    <tr>
                      <th style={{ width: '4%' }}>{ 'No.' }</th>
                      <th style={{ width: '20%' }}>{ 'Product' }</th>
                      <th style={{ width: '20%' }}>{ 'Description' }</th>
                      <th>{ 'Notes' }</th>
                      <th style={{ width: '12%' }}>{ 'Currency' }</th>
                      { showValueColumn ? <th style={{ width: '16%' }}>{ 'Value' }</th> : null }
                    </tr>
                  </thead>
                  <tbody>
                    {
                      items.map((item, index) => (
                        <tr key={ item.id || `cash-handover-item-${index}` }>
                          <td>{ index + 1 }</td>
                          <td>{ itemLabel(item) }</td>
                          <td>{ displayValue(item.description) }</td>
                          <td style={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{ displayValue(item.notes) }</td>
                          <td>{ itemCurrencyLabel(item, receipt) }</td>
                          { showValueColumn ? <td>{ item.value_label || formatCurrency(item.value, itemCurrency(item, receipt)) }</td> : null }
                        </tr>
                      ))
                    }
                    {
                      items.length === 0 ? (
                        <tr>
                          <td colSpan={ showValueColumn ? 6 : 5 }>{ 'No product/cash items.' }</td>
                        </tr>
                      ) : null
                    }
                    {
                      showValueColumn ? (
                        <tr>
                          <td colSpan={ 5 } className='font-weight-bold text-right'>{ 'Total' }</td>
                          <td className='font-weight-bold'>{ receipt.value_label || formatCurrency(receipt.value, receipt.currency) }</td>
                        </tr>
                      ) : null
                    }
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={ 12 }>
            <Card>
              <CardBody>
                <h4>{ 'Notes / Summary' }</h4>
                <Table bordered responsive size='sm' className='mb-0'>
                  <tbody>
                    { renderDetailRow('Description', receipt.description) }
                    { renderDetailRow('Notes', receipt.notes) }
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  )
}

SalesCashHandoverReceiptsDetailPage.propTypes = propTypes

export default SalesCashHandoverReceiptsDetailPage
